import { useEffect, useState } from "react";
import { useRootStore } from "../hooks/useRootStore";
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, Checkbox, CircularProgress, Divider, IconButton, Link, List, ListItem, Tooltip } from "@mui/material";
import type { Client } from "../data/Client";
import './Client.css'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Project } from "../data/Project";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { CustomerEmployeeAccessLevel, getIssueCustomerEmployeeAccessLevelNameRu } from "../data/CustomerEmployeeAccessLevel";
import { User } from "../data/User";
import PhoneIcon from '@mui/icons-material/Phone';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import SkypeIcon from "./SkypeIcon";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChangeCustomerEmployeeDialog from "./ChangeCustomerEmployeeDialog";
import { CustomerEmployee } from "../data/CustomerEmployee";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChangeClientDialog from "./ChangeClientDialog";
import { UserType } from "../data/UserType";
import { CustomerEmployeeProfile } from "../data/UserProfile";

export default function Client() {
    const { api, routerStore, userProfile } = useRootStore();
    const id = routerStore.routerState.params.id as unknown as number;

    const [loading, setLoading] = useState(true);
    const [changing, setChanging] = useState(false);
    const [employeesloading, setEmployeesLoading] = useState(true);
    const [projectsloading, setProjectsLoading] = useState(true);
    const [client, setClient] = useState({} as Client);
    const [employees, setEmployees] = useState([] as User[]);
    const [projects, setProjects] = useState([] as Project[]);

    const [onlyUsers, setOnlyUsers] = useState(false);

    const [employeeChanging, setEmployeeChanging] = useState(0);

    const [asideTop, setAsideTop] = useState(0);
    const [appStateHeight, setAppStateHeight] = useState(0);

    useEffect(() => {
        setAsideTop((document.getElementsByTagName('header')[0]).offsetHeight + 2);
        setAppStateHeight((document.getElementsByClassName('app-state')[0] as HTMLElement).offsetHeight);
    }, []);

    useEffect(() => {
        let loaded = 0;
        api.getClient(id).then(e => {
            setClient(e!);
            setLoading(false);
        });

        api.getClientEmployees(id).then(e => {
            if (onlyUsers) {                
                e = e.filter(x => x.AccessLevelID > 1)
            }
            setEmployees(e!.sort((a, b) => a.Name <= b.Name ? -1 : 1));
            setEmployeesLoading(false);
        })

        api.getClientProjects(id).then(e => {
            setProjects(e!);
            setProjectsLoading(false);
            if (++loaded == 3) {
                setLoading(false);
            }
        })

    }, [id, api, onlyUsers]);

    const projectsColumns: GridColDef[] = [
        { field: 'ID', headerName: 'Название', flex: 1, display: 'flex', renderCell: (params) => <Link href={`/projects/${params.value}`}>{params.row.Name}</Link> },
        { field: 'Archivated', headerName: 'Aрхивный', width: 150, headerAlign:'center', align: 'center', renderCell: (params) => <Checkbox readOnly checked={params.value} /> },
        { field: 'NoQueueScheduler', headerName: 'Автораспределение', headerAlign: 'center', width: 210, align: 'center', renderCell: (params) => <Checkbox readOnly checked={!params.value} /> },
    ];

    const employeesColumns: GridColDef[] = [
        { field: 'Name', headerName: 'Сотрудник', flex: 1, display: 'flex', renderCell: (params) => <Link href={`/employees/${params.row.ID}`}>{params.value}</Link> },        
        {
            field: '', headerName: 'Контакты', flex: 1, width: 210, renderCell: (params) =>
                <List>
                    {
                        <>
                            {!params.row.Phone || params.row.Phone?.trim() == '' ? '' :
                                <ListItem>
                                    <PhoneIcon fontSize="small" sx={{ color:'#333333', mr: 1 }} /><Link href={`tel:${params.row.Phone}`}>{params.row.Phone}</Link>
                                </ListItem>}
                            {!params.row.Email || params.row.Email?.trim() == '' ? '' :
                                <ListItem>
                                    <EmailIcon fontSize="small" sx={{ color: '#333333', mr: 1 }}/><Link href={`mailto:${params.row.Email}`}>{params.row.Email}</Link>
                                </ListItem>}
                            {!params.row.Telegram || params.row.Telegram?.trim() == '' ? '' :
                                <ListItem>
                                    <TelegramIcon fontSize="small" sx={{ color: '#1976d2', mr:1}} /><Link href={`https://t.me/${params.row.Telegram?.replace('@', '')}`}>{params.row.Telegram}</Link>
                                </ListItem>}
                            {!params.row.Skype || params.row.Skype?.trim() == '' ? '' :
                                <ListItem>
                                    <SkypeIcon sx={{ color: '#00a8ef', mr: 1, width: '24px', height: '24px' }} /><Link href={`skype:${params.row.Skype}?chat`}>{params.row.Skype}</Link>
                                </ListItem>}
                        </>
                    }
                </List>
        },
        { field: 'Login', headerName: 'Логин в трекере', width: 200, display: 'flex' },
        { field: 'AccessLevelID', headerName: 'Доступ', width: 150, display: 'flex', renderCell: (params) => getIssueCustomerEmployeeAccessLevelNameRu(params.value ?? CustomerEmployeeAccessLevel.none) },
        { field: 'Position', headerName: 'Должность', width: 150, display: 'flex' },
        { field: 'ID', headerName: '', width: 30, align: 'center', display: 'flex', renderCell: (params) => <IconButton className="edit-btn" sx={{ p: 0, }} onClick={() => setEmployeeChanging(params.row.ID)} title='Изменить данные сотрудника'><MoreHorizIcon /></IconButton> },
    ];

    const handleEmployeeChanged = (changed: CustomerEmployee | User) => {
        const e = employees.filter(x => x.ID != changed.ID);
        e.push(changed as User);
        setEmployees(e.sort((a, b) => a.Name <= b.Name ? -1 : 1));
        setEmployeeChanging(0);
    }

    return (
        loading ? 'Загрузка' :
            <Box className='client'>
                {changing ? <ChangeClientDialog client={client} onSave={(changed: Client) => setClient(changed)} onCancel={() => setChanging(false) } /> : ''}
                {employeeChanging == 0 ? '' : <ChangeCustomerEmployeeDialog employee={employeeChanging == -1 ? null : employees.find(x => x.ID == employeeChanging)!} onSave={handleEmployeeChanged} onCancel={() => setEmployeeChanging(0)} />}
                <Box style={{ top: asideTop, height: 'calc(100vh - ' + (asideTop + appStateHeight + 20) + 'px)' }} className="client-aside" id="client-aside">
                    <Box className='client-intro'>
                        <Box className='client-name'>{client.Name}</Box>
                        {userProfile?.UserType == UserType.CustomerEmployee
                            && (userProfile as CustomerEmployeeProfile).CustomerID == id
                            && (userProfile as CustomerEmployeeProfile).AccessLevelID == CustomerEmployeeAccessLevel.fullAccess ?
                            <IconButton
                                className="edit-btn"
                                sx={{ p: 0, position: 'absolute', top: 10, right: 20 }}
                                onClick={() => setChanging(true)}
                                title='Изменить данные клиента'
                            >
                                <MoreHorizIcon />
                            </IconButton>
                            : ''
                        }
                    </Box>
                    <Card>
                        <Box>Сайт: {client.Website ? <Link href={client.Website}>{client.Website}</Link> : ''}</Box>
                        <Box>Телефон: <Link href={`tel:${client.Phone}`}>{client.Phone}</Link></Box>
                        <Box>Email: <Link href={`mailto:${client.Email}`}>{client.Email}</Link></Box>
                        <Box>Куратор: {client.ResponsibleEmployee ? <Link href={`employees/${client.ResponsibleEmployee.ID}`}>{client.ResponsibleEmployee.Name}</Link> : ''}</Box>
                    </Card>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box flex={1} sx={{ p: 2, textAlign: 'left', }}>
                    <h2 className='client-name'>{client.Name}</h2>
                    <Accordion sx={{ mb: 3 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="employees-content"
                            id="employees-header"
                        >
                            Сотрудники: {employeesloading ? ' . . . ' : employees.length} чел.
                        </AccordionSummary>
                        <AccordionDetails>
                            {employeesloading ? <CircularProgress /> :
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Tooltip title="Добавить сотрудника">
                                            <PersonAddIcon sx={{ cursor: 'pointer', m: 2 }} onClick={() => setEmployeeChanging(-1)} />
                                        </Tooltip>
                                        <Box sx={{ ml: 4 }}>
                                            <Checkbox onChange={(e) => setOnlyUsers(e.target.checked)} />только с доступом
                                        </Box>
                                    </Box>
                                    {employees.length > 0 ?
                                        <DataGrid
                                            rows={employees}
                                            hideFooter={employees.length <= 100}
                                            getRowHeight={() => 'auto'}
                                            getRowId={(row) => row.ID}
                                            columns={employeesColumns}
                                            //initialState={{ pagination: { paginationModel } }}
                                            //pageSizeOptions={[5, 10]}
                                            // checkboxSelection
                                            sx={{ border: 0 }}
                                        />
                                        : 'нет сотрудников'
                                    }
                                </>
                            }
                        </AccordionDetails>
                    </Accordion>
                    <Accordion sx={{ mb: 3 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="employees-content"
                            id="employees-header"
                        >
                            Проекты: {projectsloading ? ' . . . ' : projects.length} шт.
                        </AccordionSummary>
                        <AccordionDetails>
                            {projectsloading ? <CircularProgress /> :
                                projects.length > 0 ?
                                    <DataGrid
                                        rows={projects}
                                        hideFooter={projects.length <= 100}
                                        getRowHeight={() => 'auto'}
                                        getRowId={(row) => row.ID}
                                        columns={projectsColumns}
                                        //initialState={{ pagination: { paginationModel } }}
                                        //pageSizeOptions={[5, 10]}
                                        // checkboxSelection
                                        sx={{ border: 0 }}
                                    />
                                    : 'нет проектов'
                            }
                        </AccordionDetails>
                    </Accordion>
                    <Accordion sx={{ mb: 3 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="employees-content"
                            id="employees-header"
                        >
                            Реквизиты
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>ИНН: {client.Inn}</Box>
                            <Box>КПП: {client.Kpp}</Box>
                            <Box>Адрес: {client.ActualAddress}</Box>
                            <Box>Юр.адрес: {client.LegalAddress}</Box>
                            <Box>Почтовый.адрес: {client.PostAddress}</Box>
                            <Box>ОКОНХ: {client.OKONH}</Box>
                            {client.BankAccounts.length == 0 ? 'Счет: не указан' :
                                <>
                                    <Box>Банк: {client.BankAccounts.length > 0 ? client.BankAccounts[0].Bank.Name : ''}</Box>
                                    <Box>Счет: {client.BankAccounts.length > 0 ? client.BankAccounts[0].AccountNumber : ''}</Box>
                                    <Box>Адрес банка: {client.BankAccounts.length > 0 ? client.BankAccounts[0].Bank.LegalAddress : ''}</Box>
                                    <Box>БИК: {client.BankAccounts.length > 0 ? client.BankAccounts[0].Bank.BIC : ''}</Box>
                                    <Box>Корр.счет: {client.BankAccounts.length > 0 ? client.BankAccounts[0].Bank.CorrAccountNumber : ''}</Box>
                                </>
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Box>
    );
}